import {
  BlockingPopupModal,
  PageWrapper,
  SaveAndCancel,
  StickyActionsHeader,
} from "@/components";
import { useProjectRoleContext } from "@/containers/Admin/ProjectRoles/ProjectRoleProvider";
import { NewProjectRole } from "@/containers/Admin/ProjectRoles/components/NewProjectRole";
import ErrorModal from "@/containers/ErrorModal";
import { IErrorProps } from "@/containers/ErrorModal/interfaces";
import { routes } from "@/helpers";
import { IProjectRole } from "@/interfaces";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

const emptyRole: IProjectRole = {
  createdAt: "",
  createdBy: "",
  updatedAt: "",
  updatedBy: "",
  assignedUsers: [],
  availableUsers: [],
  id: "",
  isDeleted: false,
  isActive: true,
  isMilestoneEditor: false,
  name: "",
  projectTypeIds: [],
  regionSpecificTemplateRoles: [],
  regionalProjectRoles: [],
  regions: {},
};

export const NewProjectRolePage = () => {
  const navigate = useNavigate();
  const intl = useIntl();

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [cancelClicked, setCancelClicked] = useState<boolean>(false);
  const [errorModal, setErrorModal] = useState<IErrorProps>({
    visible: false,
    title: "",
    description: "",
    errors: [],
  });

  const {
    projectRole,
    createProjectRole,
    projectRoles,
    createRoleSaving,
    hasErrors,
  } = useProjectRoleContext();
  const [originalProjectRole, setOriginalProjectRole] =
    useState<IProjectRole>(projectRole);

  const checkForChanges = (projectRole: IProjectRole): boolean =>
    JSON.stringify(projectRole) !== JSON.stringify(originalProjectRole);

  useEffect(() => {
    if (!projectRole?.id) return;
    if (!originalProjectRole) return;

    setHasUnsavedChanges(checkForChanges(projectRole));
  }, [projectRole, checkForChanges, originalProjectRole]);

  useEffect(() => {
    if (cancelClicked) {
      navigate(routes.admin.projectRoles.list);
    }
  }, [cancelClicked, navigate]);

  const handleSaveProjectRole = (): void => {
    if (!projectRoles) return;
    if (projectRole == null || projectRole.name.trim() === "") return;

    // Check if a role with the same name already exists
    const exists = (projectRoles ?? []).some(
      (role: { name: string }) =>
        role?.name?.toLowerCase?.() === projectRole?.name?.toLowerCase?.()
    );

    if (exists) {
      setErrorModal({
        visible: true,
        title: intl.formatMessage(
          { id: `projectRoles.errorModal.notSavedTitle` },
          { projectRoleName: projectRole.name }
        ),
        description: intl.formatMessage({
          id: `projectRoles.errorModal.duplicateNameErrorMessage`,
        }),
        errors: [],
      });
    } else {
      createProjectRole(projectRole);
      setOriginalProjectRole(projectRole);
      setHasUnsavedChanges(false);
    }
  };

  const handleCancelClick = (): void => {
    setCancelClicked(true);
    setHasUnsavedChanges(false);
  };

  const handleCloseModal = (): void => {
    setErrorModal({
      visible: false,
      title: "",
      description: "",
      errors: [],
    });
  };

  return (
    <>
      <BlockingPopupModal
        title={intl.formatMessage(
          { id: `projectRoles.errorModal.notSavedTitle` },
          {
            projectRoleName: projectRole?.name,
          }
        )}
        okText={intl.formatMessage({ id: "general.yes" })}
        onOk={handleSaveProjectRole}
        message={intl.formatMessage({
          id: "projectRoles.errorModal.unsavedErrorMessage",
        })}
        isBlocking={hasUnsavedChanges}
      />
      <PageWrapper
        pageTitle={intl.formatMessage({ id: "projectRoles.newTitle" })}
        backButtonRoute={routes.admin.projectRoles.list}
        pageActions={
          <StickyActionsHeader
            rightChildren={
              <SaveAndCancel
                pageName="template-role-editor"
                cancelOnClick={handleCancelClick}
                saveOnClick={handleSaveProjectRole}
                saveDisabled={hasErrors}
                loading={createRoleSaving}
              />
            }
          />
        }
      >
        <NewProjectRole
          role={emptyRole}
          handleUnsavedChanges={setHasUnsavedChanges}
        />
      </PageWrapper>

      <ErrorModal modalState={errorModal} closeModal={handleCloseModal} />
    </>
  );
};
